//core
import React, {Dispatch, SetStateAction, useContext, useEffect, useState} from "react";
import { useTranslation } from "react-i18next";
import format from "date-fns/format";
import { endOfDay, startOfDay } from "date-fns";
import { useSelector } from "react-redux";
import { useLocalStorage } from "usehooks-ts";
import {
  Box,
  SelectChangeEvent,
  useMediaQuery,
} from "@mui/material";

//styles
import { useStyles } from "./styles";

//helpers
import {getDataIds, getGroupByOptions, getPeriod, PeriodTypes} from "../../../../../utils";

//constant
import { summaryPeriodOptions } from "../../../../../constant";
import { favoriteFilterKeys } from "../../../../constants";

//selectors
import {
  selectDealsData,
  selectOptionsData,
  selectPlatformsData
} from "../../../../../../../../store/admin/reports/agent_statistic_report/selectors";

//hooks
import { useAgentStatisticReport } from "../../../../../../../../store/admin/reports/agent_statistic_report/useAgentStatisticReport";
import { useRegistration } from "../../../../../../../../store/common/registration/useRegistration";

//context
import { GlobalContext } from "../../../../../../../../context/GlobalContext";

//components
import CSelect from "../../../../../../../UI/Fields/CSelect";
import CDateRangePicker from "../../../../../../../UI/Fields/CDateRangePicker";
import GroupByField, { IItemOption } from "../../../GroupByField";
import MuiAutocompleteSelectLazy from "../../../../../../../UI/Fields/MuiAutocompleteSelectLazy";
import { IAgentStatReportFilterDataType } from "../../../../types";
import MuiAutocompleteSelect from "../../../../../../../UI/Fields/MuiAutocompleteSelect";
import MuiChipsInputField from "../../../../../../../UI/Fields/MuiChipsInput";
import {hasRoleLocalization} from "../../../../../../../../constants/roleLocalizationConfig";


interface IVisibleFilterProps {
  filter: IAgentStatReportFilterDataType;
  setUpdateFilter: Dispatch<SetStateAction<IAgentStatReportFilterDataType>>;
}

const VisibleFilter: React.FC<IVisibleFilterProps> = ({
  filter,
  setUpdateFilter,
}): JSX.Element => {
  const { t, i18n } = useTranslation();
  const styles = useStyles();
  const mobile = useMediaQuery('(max-width:767px)');

  const {
    getBrands,
    getCurrencies,
    getAgents,
    getAgentRoles,
    getPlayers,
  } = useAgentStatisticReport();
  const { countries } = useRegistration();

  const context = useContext(GlobalContext);

  const optionsData = useSelector(selectOptionsData);
  const dealsData = useSelector(selectDealsData);
  const platformsData = useSelector(selectPlatformsData);

  //state
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodTypes>(filter.period);
  const [startDate, setStartDate] = useState<Date | ''>(getPeriod(filter.period).start);
  const [endDate, setEndDate] = useState<Date | ''>(getPeriod(filter.period).end);

  const [registrationFrom, setRegistrationFrom] = useState<Date | ''>(!!filter.registrationFrom ? new Date(filter.registrationFrom) : '');
  const [registrationTo, setRegistrationTo] = useState<Date | ''>(!!filter.registrationTo ? new Date(filter.registrationTo) : '');

  const [firstDepositFrom, setFirstDepositFrom] = useState<Date | ''>(!!filter.firstDepositFrom ? new Date(filter.firstDepositFrom) : '');
  const [firstDepositTo, setFirstDepositTo] = useState<Date | ''>(!!filter.firstDepositTo ? new Date(filter.firstDepositTo) : '');

  const [cpaQualificationFrom, setCpaQualificationFrom] = useState<Date | ''>(!!filter.cpaQualificationFrom ? new Date(filter.cpaQualificationFrom) : '');
  const [cpaQualificationTo, setCpaQualificationTo] = useState<Date | ''>(!!filter.cpaQualificationTo ? new Date(filter.cpaQualificationTo) : '');

  const [groupBy, setGroupBy] = useState<IItemOption[]>(filter.group_by.fields);

  const [dataCountries, setDataCountries] = useState<any | null>(null);

  //favorite fields state
  const [favAdmSRAgentIds, setFavAdmSRAgentIds] = useLocalStorage<boolean>(favoriteFilterKeys.agentIds, false);
  const [favAdmSRAgentRoles, setFavAdmSRAgentRoles] = useLocalStorage<boolean>(favoriteFilterKeys.agentRoles, false);

  const [favAdmSRProductIds, setFavAdmSRProductIds] = useLocalStorage<boolean>(favoriteFilterKeys.productIds, false);
  const [favAdmSRPlatformIds, setFavAdmSRPlatformIds] = useLocalStorage<boolean>(favoriteFilterKeys.platformIds, false);
  const [favAdmSRManagerIds, setFavAdmSRManagerIds] = useLocalStorage<boolean>(favoriteFilterKeys.managerIds, false);
  const [favAdmSRAffiliateIds, setFavAdmSRAffiliateIds] = useLocalStorage<boolean>(favoriteFilterKeys.affiliateIds, false);
  const [favAdmSRTrackerIds, setFavAdmSRTrackerIds] = useLocalStorage<boolean>(favoriteFilterKeys.trackerIds, false);
  const [favAdmSRCreativeIds, setFavAdmSRCreativeIds] = useLocalStorage<boolean>(favoriteFilterKeys.creativeIds, false);
  const [favAdmSRBrands, setFavAdmSRBrands] = useLocalStorage<boolean>(favoriteFilterKeys.brands, false);
  const [favAdmSRCurrencies, setFavAdmSRCurrencies] = useLocalStorage<boolean>(favoriteFilterKeys.currencies, false);
  const [favAdmSRDealTypes, setFavAdmSRDealTypes] = useLocalStorage<boolean>(favoriteFilterKeys.dealTypes, false);
  const [favAdmSRAnid, setFavAdmSRAnid] = useLocalStorage<boolean>(favoriteFilterKeys.anid, false);
  const [favAdmSRPlayerId, setFavAdmSRPlayerId] = useLocalStorage<boolean>(favoriteFilterKeys.playerId, false);
  const [favAdmSRSource1, setFavAdmSRSource1] = useLocalStorage<boolean>(favoriteFilterKeys.source1, false);
  const [favAdmSRSource2, setFavAdmSRSource2] = useLocalStorage<boolean>(favoriteFilterKeys.source2, false);
  const [favAdmSRSource3, setFavAdmSRSource3] = useLocalStorage<boolean>(favoriteFilterKeys.source3, false);
  const [favAdmSRSource4, setFavAdmSRSource4] = useLocalStorage<boolean>(favoriteFilterKeys.source4, false);
  const [favAdmSRSource5, setFavAdmSRSource5] = useLocalStorage<boolean>(favoriteFilterKeys.source5, false);
  const [favAdmSRDeviceTypes, setFavAdmSRDeviceTypes] = useLocalStorage<boolean>(favoriteFilterKeys.deviceTypes, false);
  const [favAdmSRGeo, setFavAdmSRGeo] = useLocalStorage<boolean>(favoriteFilterKeys.geo, false);

  useEffect(() => {
    if (!!selectedPeriod) {
      const period = getPeriod(selectedPeriod);

      if (!!period.start && !!period.end) {
        setStartDate(period.start);
        setEndDate(period.end);

        context.setAdmAgentStatisticReportFilter({
          ...context.admAgentStatisticReportFilter,
          period: selectedPeriod,
          sent_from: format(startOfDay(new Date(period.start)), "yyyy-MM-dd HH:mm:ss"),
          sent_to: format(endOfDay(new Date(period.end)), "yyyy-MM-dd HH:mm:ss"),
        });
        setUpdateFilter((prevState: any) => {
          return {
            ...prevState,
            period: selectedPeriod,
            sent_from: format(startOfDay(new Date(period.start)), "yyyy-MM-dd HH:mm:ss"),
            sent_to: format(endOfDay(new Date(period.end)), "yyyy-MM-dd HH:mm:ss"),
          }
        })
      }
    }
  }, [selectedPeriod]);

  useEffect(() => {
    setSelectedPeriod(filter.period);
  }, [filter.period]);

  useEffect(() => {
    setGroupBy(filter.group_by.fields);
  }, [filter.group_by.fields]);

  useEffect(() => {
    setUpdateFilter((prevState) => {
      return {
        ...prevState,
        group_by: {
          fields: groupBy,
        },
      }
    })
  }, [groupBy]);

  useEffect(() => {
    if (!!countries.data) {
      const data = countries.data.reduce((acc: any[], item: any) => {
        acc.push({
          id: item.code,
          value: item.code,
        });
        return acc;
      }, []);
      setDataCountries({
        list: data,
        total_entries: data.length,
      });
    }
  }, [countries, i18n.language]);

  useEffect(() => {
    context.setAdmAgentStatisticReportFilter(filter);
  }, [filter]);

  useEffect(() => {
    setRegistrationFrom(!!filter.registrationFrom ? new Date(filter.registrationFrom) : '');
    setRegistrationTo(!!filter.registrationTo ? new Date(filter.registrationTo) : '');
  }, [filter.registrationFrom, filter.registrationTo]);

  useEffect(() => {
    setFirstDepositFrom(!!filter.firstDepositFrom ? new Date(filter.firstDepositFrom) : '');
    setFirstDepositTo(!!filter.firstDepositTo ? new Date(filter.firstDepositTo) : '');
  }, [filter.firstDepositFrom, filter.firstDepositTo]);

  useEffect(() => {
    setCpaQualificationFrom(!!filter.cpaQualificationFrom ? new Date(filter.cpaQualificationFrom) : '');
    setCpaQualificationTo(!!filter.cpaQualificationTo ? new Date(filter.cpaQualificationTo) : '');
  }, [filter.cpaQualificationFrom, filter.cpaQualificationTo]);

  const handleChangePeriodSelect = (event: SelectChangeEvent) => {
    //@ts-ignore
    const value: PeriodTypes = event.target.value;
    setSelectedPeriod(value);
  };

  const handleChangeDates = (start: Date, end: Date) => {
    const noEndDate = !end;

    setStartDate(start);
    setEndDate(noEndDate ? start : end);
    context.setAdmAgentStatisticReportFilter({
      ...context.admAgentStatisticReportFilter,
      period: '',
      sent_from: format(startOfDay(new Date(start)), "yyyy-MM-dd HH:mm:ss"),
      sent_to: format(endOfDay(new Date(noEndDate ? start : end)), "yyyy-MM-dd HH:mm:ss"),
    });
    setUpdateFilter((prevState: any) => {
      return {
        ...prevState,
        period: '',
        sent_from: format(startOfDay(new Date(start)), "yyyy-MM-dd HH:mm:ss"),
        sent_to: format(endOfDay(new Date(noEndDate ? start : end)), "yyyy-MM-dd HH:mm:ss"),
      }
    });
  };

  const fieldFilterPayload = () => {
    return {
      page: 1,
      search: null,
      perPage: 100,
      isViewDataOfAllAgents: filter.isViewDataOfAllAgents
    }
  };

  const favoriteFields = !!dataCountries && !!optionsData && !mobile &&
  (favAdmSRAgentIds || favAdmSRAgentRoles || favAdmSRProductIds || favAdmSRManagerIds || favAdmSRAffiliateIds || favAdmSRDealTypes || favAdmSRTrackerIds ||
    favAdmSRCreativeIds || favAdmSRBrands || favAdmSRCurrencies || favAdmSRPlatformIds || favAdmSRAnid ||
    favAdmSRPlayerId || favAdmSRSource1 || favAdmSRSource2 || favAdmSRSource3 || favAdmSRSource4 || favAdmSRSource5 || favAdmSRDeviceTypes || favAdmSRGeo)
    ? (
      <Box className={styles.favoriteFields}>
        {optionsData.available_filters.includes("platformIds") && (<>
          {favAdmSRPlatformIds && (
            <>
              {!!platformsData && platformsData.list.length > 1 && (
                <Box>
                  <MuiAutocompleteSelect
                    keyValue="value"
                    keyId="id"
                    id="platformIds"
                    name="platformIds"
                    label={t(`admin.reports.filter.platformIds`)}
                    value={filter.platformIds}
                    multiple={true}
                    showSelectAll={false}
                    disabled={false}
                    staticOptions={platformsData.list}
                    onChange={setUpdateFilter}
                    disableCloseOnSelect={true}
                    maxHeightFieldScroll={32}
                    showLabelCounter={true}
                  />
                </Box>
              )}
            </>
          )}
        </>)}

        {optionsData.available_filters.includes("agentIds") && (<>
          {favAdmSRAgentIds && (<>
            <Box>
              <MuiAutocompleteSelectLazy
                keyValue="value"
                keyId="id"
                id="agentIds"
                name="agentIds"
                label={t(`admin.reports.filter.agentIds`)}
                value={filter.agentIds}
                multiple={true}
                showSelectAll={false}
                disabled={false}
                getOptions={getAgents}
                payloadData={fieldFilterPayload()}
                labelWithId={true}
                onChange={setUpdateFilter}
                disableCloseOnSelect={true}
                maxHeightFieldScroll={32}
                showLabelCounter={true}
              />
            </Box>
          </>)}
        </>)}

        {optionsData.available_filters.includes("agentRoleIds") && (<>
          {favAdmSRAgentRoles && (<>
            <Box>
              <MuiAutocompleteSelectLazy
                keyValue="value"
                keyId="id"
                id="agentRoles"
                name="agentRoles"
                label={t(`admin.reports.filter.agentRoles`)}
                value={filter.agentRoles}
                multiple={true}
                showSelectAll={false}
                disabled={false}
                getOptions={getAgentRoles}
                payloadData={fieldFilterPayload()}
                labelWithId={true}
                onChange={setUpdateFilter}
                disableCloseOnSelect={true}
                maxHeightFieldScroll={32}
                showLabelCounter={true}
                {...(hasRoleLocalization() ? { prefix: "common.roles." } : {})}
              />
            </Box>
          </>)}
        </>)}

        {optionsData.available_filters.includes("geos") && (<>
          {favAdmSRGeo && (
            <MuiAutocompleteSelect
              keyValue="value"
              keyId="id"
              id="geo"
              name="geo"
              label={t(`admin.reports.filter.geo`)}
              value={filter.geo}
              multiple={true}
              showSelectAll={false}
              disabled={false}
              staticOptions={dataCountries.list}
              onChange={setUpdateFilter}
              disableCloseOnSelect={true}
              maxHeightFieldScroll={32}
              showLabelCounter={true}
              prefix={`common.components.creative.list_geolocales.`}
            />
          )}
        </>)}

        {optionsData.available_filters.includes("brands") && (<>
          {favAdmSRBrands && (
            <Box>
              <MuiAutocompleteSelectLazy
                keyValue="value"
                keyId="id"
                id="brands"
                name="brands"
                label={t(`admin.reports.filter.brands`)}
                value={filter.brands}
                multiple={true}
                showSelectAll={false}
                disabled={false}
                getOptions={getBrands}
                payloadData={fieldFilterPayload()}
                labelWithId={true}
                onChange={setUpdateFilter}
                disableCloseOnSelect={true}
                maxHeightFieldScroll={32}
                showLabelCounter={true}
              />
            </Box>
          )}
        </>)}

        {optionsData.available_filters.includes("currencies") && (<>
          {favAdmSRCurrencies && (
            <Box>
              <MuiAutocompleteSelectLazy
                keyValue="value"
                keyId="id"
                id="currencies"
                name="currencies"
                label={t(`admin.reports.filter.currencies`)}
                value={filter.currencies}
                multiple={true}
                showSelectAll={false}
                disabled={false}
                getOptions={getCurrencies}
                payloadData={fieldFilterPayload()}
                labelWithId={false}
                onChange={setUpdateFilter}
                disableCloseOnSelect={true}
                maxHeightFieldScroll={32}
                showLabelCounter={true}
              />
            </Box>
          )}
        </>)}

        {optionsData.available_filters.includes("playerIds") && (<>
          {favAdmSRPlayerId && (
            <Box>
              <MuiAutocompleteSelectLazy
                keyValue="value"
                keyId="id"
                id="playerId"
                name="playerId"
                label={t(`admin.reports.filter.playerId`)}
                value={filter.playerId}
                multiple={true}
                showSelectAll={false}
                disabled={false}
                getOptions={getPlayers}
                payloadData={fieldFilterPayload()}
                labelWithId={false}
                onChange={setUpdateFilter}
                disableCloseOnSelect={true}
                maxHeightFieldScroll={32}
                showLabelCounter={true}
              />
            </Box>
          )}
        </>)}


        {/*other*/}
        {optionsData.available_filters.includes("anid") && (<>
          {favAdmSRAnid && (
            <Box>
              <MuiChipsInputField
                id="anid"
                name="anid"
                label={t(`admin.reports.filter.anid`)}
                value={filter.anid}
                onChange={setUpdateFilter}
              />
            </Box>
          )}
        </>)}
        {optionsData.available_filters.includes("source1") && (<>
          {favAdmSRSource1 && (
            <Box>
              <MuiChipsInputField
                id="source1"
                name="source1"
                label={t(`admin.reports.filter.source1`)}
                value={filter.source1}
                onChange={setUpdateFilter}
              />
            </Box>
          )}
        </>)}
        {optionsData.available_filters.includes("source2") && (<>
          {favAdmSRSource2 && (
            <Box>
              <MuiChipsInputField
                id="source2"
                name="source2"
                label={t(`admin.reports.filter.source2`)}
                value={filter.source2}
                onChange={setUpdateFilter}
              />
            </Box>
          )}
        </>)}
        {optionsData.available_filters.includes("source3") && (<>
          {favAdmSRSource3 && (
            <Box>
              <MuiChipsInputField
                id="source3"
                name="source3"
                label={t(`admin.reports.filter.source3`)}
                value={filter.source3}
                onChange={setUpdateFilter}
              />
            </Box>
          )}
        </>)}
        {optionsData.available_filters.includes("source4") && (<>
          {favAdmSRSource4 && (
            <Box>
              <MuiChipsInputField
                id="source4"
                name="source4"
                label={t(`admin.reports.filter.source4`)}
                value={filter.source4}
                onChange={setUpdateFilter}
              />
            </Box>
          )}
        </>)}
        {optionsData.available_filters.includes("source5") && (<>
          {favAdmSRSource5 && (
            <Box>
              <MuiChipsInputField
                id="source5"
                name="source5"
                label={t(`admin.reports.filter.source5`)}
                value={filter.source5}
                onChange={setUpdateFilter}
              />
            </Box>
          )}
        </>)}

      </Box>
    ) : null;

  return (<>
    {!!dataCountries && !!optionsData && (
      <Box>
        <Box className={styles.wrapper}>
          <Box className={styles.box}>
            <CSelect
              name="period"
              label={t("admin.reports.filter.period")}
              value={selectedPeriod}
              options={summaryPeriodOptions}
              onChange={handleChangePeriodSelect}
              prefix="common.components.periods."
            />
          </Box>
          <Box className={styles.boxDate}>
            <CDateRangePicker
              startDate={startDate}
              endDate={endDate}
              onChange={handleChangeDates}
            />
          </Box>
        </Box>
        <Box className={styles.field}>
          <GroupByField
            name="groupBy"
            value={filter.group_by.fields}
            options={getGroupByOptions(optionsData.group_by)}
            setFilter={setGroupBy}
            prefix={"admin.reports.filter.group_by_select."}
          />
        </Box>
        {favoriteFields}
      </Box>
    )}
  </>);
};

export default VisibleFilter;
